import { useCallback, forwardRef, Ref, RefAttributes, type JSX } from "react";

import { SxProps, Theme, useTheme } from "@mui/material/styles";
import { DesktopDatePicker as MuiDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { DateValidationError, PickerChangeHandlerContext } from "@mui/x-date-pickers/models";
import { DateTime } from "luxon";
import { FieldValues, useController } from "react-hook-form";

import { BaseFormDatePickerFieldProps, FormDatePickerFieldProps, SubscribedFormDatePickerFieldProps } from "./types";

import { FormButton } from "@/forms-design-system/FormButton/FormButton";
import { FormField, getFormFieldProps } from "@/forms-design-system/FormField";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { mergeSx } from "@/helpers/mergeSx";
import { useValidators } from "@/hooks/useValidators";
import { ChevronRight, ChevronLeft, Date, ChevronDown } from "@/icons/outlined";

function FormDatePickerFieldInner<T extends FieldValues>(
  { control, rules, name, ...rest }: FormDatePickerFieldProps<T>,
  ref: Ref<HTMLDivElement>
) {
  return control && name ? (
    <SubscribedFormDatePickerField {...rest} ref={ref} control={control} name={name} rules={rules} />
  ) : (
    <BaseFormDatePickerField {...rest} name={name} ref={ref} />
  );
}

FormDatePickerFieldInner.displayName = "FormDatePickerField";

export const FormDatePickerField = forwardRef(FormDatePickerFieldInner) as <T extends FieldValues>(
  props: FormDatePickerFieldProps<T> & RefAttributes<HTMLDivElement>
) => JSX.Element;

function SubscribedFormDatePickerFieldInner<T extends FieldValues>(
  {
    control,
    defaultValue,
    name,
    onBlur,
    onChange,
    rules,
    shouldUnregister,
    inputRef,
    ...rest
  }: SubscribedFormDatePickerFieldProps<T>,
  ref: Ref<HTMLDivElement>
) {
  const validators = useValidators(rules);

  const { field, fieldState } = useController<T>({
    name,
    control,
    defaultValue: defaultValue as never,
    rules: validators,
    shouldUnregister,
  });

  const handleChange = useCallback(
    (date: Date | null) => {
      onChange?.(date);
      field.onChange(date);
    },
    [field, onChange]
  );

  const handleBlur = useCallback(() => {
    onBlur?.();
    field.onBlur();
  }, [field, onBlur]);

  return (
    <BaseFormDatePickerField
      {...rest}
      error={Boolean(fieldState.error)}
      helperText={fieldState.error?.message}
      value={field.value || null}
      inputRef={inputRef}
      ref={ref}
      onBlur={handleBlur}
      onChange={handleChange}
      data-test={rest?.["data-test"] || "form-date-picker-field"}
    />
  );
}

const SubscribedFormDatePickerField = forwardRef(SubscribedFormDatePickerFieldInner) as <T extends FieldValues>(
  props: SubscribedFormDatePickerFieldProps<T> & RefAttributes<HTMLDivElement>
) => JSX.Element;

const BaseFormDatePickerField = forwardRef<HTMLDivElement, BaseFormDatePickerFieldProps>(
  ({ inputRef, defaultValue, value, disabled, error, onChange, onBlur, ...rest }, ref) => {
    const theme = useTheme();

    const handleOnChange = useCallback(
      (date: DateTime | null, context: PickerChangeHandlerContext<DateValidationError>) => {
        onChange?.(date?.toJSDate() ?? null, context);
      },
      [onChange]
    );

    if (
      !theme.palette.forms ||
      !theme.palette.formsComponents?.global ||
      !theme.palette.formsComponents?.datePickerField
    ) {
      return <InvalidThemeErrorMessage />;
    }

    const luxonDefaultDate = defaultValue ? DateTime.fromJSDate(defaultValue) : undefined;
    const luxonValue = value !== null && value !== undefined ? DateTime.fromJSDate(value) : null;

    const datePickerStyles = mergeSx({
      ["& .MuiInputBase-root"]: {
        height: "38px",
        ...theme.typography.body2,
        color: theme.palette.formsComponents.datePickerField.textColor,
        backgroundColor: theme.palette.formsComponents.datePickerField.backgroundColor,
        border: `1px solid ${theme.palette.formsComponents.datePickerField.borderColor}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(1, 0.5, 1, 1),
        transition: "outline 100ms ease-out, border-color 200ms ease-out",
        outlineColor: "transparent",
        ["&.Mui-disabled"]: {
          opacity: 0.4,
        },
        ...(error && {
          borderColor: theme.palette.formsComponents.datePickerField.errorBorderColor,
        }),
        ["&:hover:not(.Mui-disabled)"]: {
          borderColor: theme.palette.formsComponents.datePickerField.hoverBorderColor,
        },
        ["&.Mui-focused"]: {
          borderColor: theme.palette.formsComponents.datePickerField.hoverBorderColor,
          outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
        },
      },
      ["& .MuiInputBase-input"]: {
        padding: 0,
        ["&::placeholder"]: {
          color: theme.palette.formsComponents.datePickerField.placeholderColor,
        },
        ["&.Mui-disabled"]: {
          "-webkit-text-fill-color": theme.palette.formsComponents.selectField.textColor, // Override MUI default color
        },
      },
      ["& .MuiOutlinedInput-notchedOutline"]: {
        display: "none",
      },
    });

    return (
      <FormField ref={ref} error={error} disabled={disabled} data-test={rest["data-test"]} {...getFormFieldProps(rest)}>
        <MuiDatePicker<DateTime>
          inputRef={inputRef}
          {...(luxonDefaultDate ? { defaultValue: luxonDefaultDate } : { value: luxonValue })}
          disabled={disabled}
          onClose={onBlur}
          onChange={handleOnChange}
          slots={{
            openPickerButton: ({ onClick }) => (
              <FormButton
                variant="ghost"
                startIcon={<Date />}
                iconButton
                sx={{
                  flex: "0 0 auto",
                  padding: 0,
                  width: 28,
                  height: 28,
                  minHeight: 0, // Override MUI default style
                }}
                onClick={onClick}
                disabled={disabled}
              />
            ),
            previousIconButton: ({ onClick }: { onClick: () => void }) => (
              <FormButton variant="ghost" iconButton onClick={onClick} startIcon={<ChevronLeft />} />
            ),
            nextIconButton: ({ onClick }: { onClick: () => void }) => (
              <FormButton variant="ghost" iconButton onClick={onClick} startIcon={<ChevronRight />} />
            ),
            switchViewButton: ({ onClick, sx }: { onClick: () => void; sx: SxProps<Theme> }) => (
              <FormButton variant="ghost" iconButton onClick={onClick} startIcon={<ChevronDown />} sx={sx} />
            ),
          }}
          slotProps={{
            textField: { label: undefined, onBlur },
            desktopPaper: {
              sx: {
                backgroundColor: theme.palette.formsComponents.datePickerField.popoverBackgroundColor,
                ["& .MuiPickersArrowSwitcher-spacer"]: {
                  width: 8,
                },
                ["& .MuiYearCalendar-root"]: {
                  scrollbarColor: `${theme.palette.formsComponents.global.scrollbarThumbColor} transparent`, // We leave the track transparent to avoid issues with padding
                  ["&::-webkit-scrollbar-track"]: {
                    background: "transparent",
                  },
                  ["&::-webkit-scrollbar-thumb"]: {
                    background: theme.palette.formsComponents.global.scrollbarThumbColor,
                  },
                },
                ["& .MuiDayCalendar-weekDayLabel"]: {
                  color: theme.palette.formsComponents.datePickerField.dayOfWeekTextColor,
                },
                ["& .MuiPickersYear-yearButton"]: {
                  color: theme.palette.formsComponents.datePickerField.dayTextColor,
                  "&.Mui-selected": {
                    color: theme.palette.formsComponents.datePickerField.selectedDayTextColor,
                    backgroundColor: theme.palette.formsComponents.datePickerField.selectedDayBackgroundColor,
                  },
                  ["&:hover, &:focus, &.Mui-selected:hover, &.Mui-selected:focus"]: {
                    color: theme.palette.formsComponents.datePickerField.dayHoverTextColor,
                    backgroundColor: theme.palette.formsComponents.datePickerField.dayHoverBackgroundColor,
                  },
                },
              },
            },
            day: {
              disableRipple: true,
              sx: {
                color: theme.palette.formsComponents.datePickerField.dayTextColor,
                "&.Mui-selected": {
                  color: theme.palette.formsComponents.datePickerField.selectedDayTextColor,
                  backgroundColor: theme.palette.formsComponents.datePickerField.selectedDayBackgroundColor,
                },
                ["&:hover, &:focus, &.Mui-selected:hover, &.Mui-selected:focus"]: {
                  color: theme.palette.formsComponents.datePickerField.dayHoverTextColor,
                  backgroundColor: theme.palette.formsComponents.datePickerField.dayHoverBackgroundColor,
                },
                ["& .MuiPickersDay-today"]: {
                  borderColor: theme.palette.formsComponents.datePickerField.todayBorderColor,
                  backgroundColor: "transparent",
                },
              },
            },
            switchViewButton: {
              sx: {
                width: 28,
                height: 28,
                minHeight: 0, // Override MUI default style
              },
            },
            calendarHeader: {
              sx: {
                ["& .MuiPickersCalendarHeader-labelContainer"]: {
                  color: theme.palette.formsComponents.datePickerField.headerTextColor,
                  padding: theme.spacing(0.5, 0.5, 0.5, 0),
                },
              },
            },
          }}
          sx={datePickerStyles}
        />
      </FormField>
    );
  }
);

BaseFormDatePickerField.displayName = "BaseFormDatePickerField";
